import React, { useState, useEffect } from 'react';
import { cachedInvoke } from '../../lib/apiCache';
import { isSocialUrl } from '../../lib/socialLinks';

const DOWp = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONp = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function prettyDate(iso) {
  const d = new Date(iso);
  if (!iso || iso === '—' || isNaN(d)) return '—';
  return `${DOWp[d.getDay()]}, ${MONp[d.getMonth()]} ${d.getDate()}`;
}

function prettyTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return `${MONp[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function labelFor(url) {
  try {
    const h = new URL(url).hostname.replace('www.', '');
    const m = h.split('.')[0];
    return m.charAt(0).toUpperCase() + m.slice(1);
  } catch {
    return 'Link';
  }
}

function LinkBtn({ href, children, muted }) {
  if (!href) return null;
  return (
    <a className={`linkbtn ${muted ? 'muted' : ''}`} href={href} target="_blank" rel="noopener noreferrer">
      {children} <span className="linkbtn-arrow">↗</span>
    </a>
  );
}

// JamBase event/festival detail drawer. `item` is the summary record from the
// search feed (n, id, iso, img, …); on open it fetches the full record by id,
// falling back to a name search when there is no id.
export default function FestivalDrawer({ item, onClose }) {
  const [imgOk, setImgOk] = useState(true);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  const fetchDetail = (id, alive) =>
    cachedInvoke('lyveDmsFestival', { id }).then((r) => {
      if (!alive) return null;
      return r && r.data ? r.data.festival : null;
    });

  const fetchByName = async (name, alive) => {
    const r = await cachedInvoke('lyveDmsFestivals', { q: name, pages: 1 });
    if (!alive) return null;
    const fs = (r && r.data && r.data.festivals) || [];
    const lc = name && name.toLowerCase();
    return (
      (fs.find((f) => f.n && f.n.toLowerCase() === lc) ||
        fs.find((f) => f.n && f.n.toLowerCase().includes(lc)) ||
        fs[0]) ||
      null
    );
  };

  useEffect(() => {
    setImgOk(true);
    setDetail(null);
    setErr(false);
    let alive = true;
    const id = item && item.id;
    const name = item && item.n;
    if (!id && !name) return undefined;
    setLoading(true);
    (async () => {
      let f = null;
      if (id) f = await fetchDetail(id, alive).catch(() => null);
      if (!f && name) {
        const found = await fetchByName(name, alive).catch(() => null);
        if (found && found.id) f = await fetchDetail(found.id, alive).catch(() => null);
        if (!f) f = found;
      }
      if (!alive) return;
      if (!f) setErr(true);
      else setDetail(f);
    })()
      .catch(() => {
        if (alive) setErr(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item && (item.id || item.n)]);

  const d = detail || item || {};
  const sub = `${prettyDate(d.iso)} — ${prettyDate(d.end)} · ${d.eventStatus || 'scheduled'}`;
  const performers = d.performers || [];
  const offers = d.offers || [];
  const organizers = Array.isArray(d.organizer) ? d.organizer : d.organizer ? [d.organizer] : [];
  const sponsors = d.sponsors || [];
  const sameAs = d.sameAs || [];

  const links = sameAs.filter((u) => isSocialUrl(u)).map((u) => ({ label: labelFor(u), href: u }));

  const perfPill = (p, i) => {
    const inner = (
      <>
        {p.name}
        {p.headliner ? <span className="hlbadge">HEADLINER</span> : null}
        {p.genre ? <span className="gn">{p.genre}</span> : null}
      </>
    );
    return p.url ? (
      <a key={i} className={`perf ${p.headliner ? 'hl' : ''}`} href={p.url} target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    ) : (
      <span key={i} className={`perf ${p.headliner ? 'hl' : ''}`}>
        {inner}
      </span>
    );
  };

  return (
    <div className="ea-root fdrawer">
      <div className="bg" onClick={onClose} />
      <div className="panel">
        <div className="fhead">
          <div>
            <h2 className="ftitle">{d.n || '—'}</h2>
            <p className="fsub">{sub}</p>
          </div>
          <button className="fclose" onClick={onClose}>
            ✕ Close
          </button>
        </div>

        {loading ? <div className="fload">Loading full details…</div> : null}
        {err && !loading && performers.length === 0 && !d.timezone ? (
          <div className="fload" style={{ color: 'var(--ea-amber)' }}>
            Full details unavailable — showing summary.
          </div>
        ) : null}

        <div className="fhero">
          {d.img && imgOk ? (
            <img src={d.img} alt={d.n} onError={() => setImgOk(false)} />
          ) : (
            <div className="ph">No image available</div>
          )}
        </div>

        {d.description ? <p className="fdesc">{d.description}</p> : null}

        <div className="fsec">
          <div className="fsec-h">When</div>
          <div className="fkv">
            <span className="k">Starts</span>
            <span className="v">{prettyDate(d.iso)}</span>
          </div>
          <div className="fkv">
            <span className="k">Ends</span>
            <span className="v">{prettyDate(d.end)}</span>
          </div>
          <div className="fkv">
            <span className="k">Timezone</span>
            <span className="v">{d.timezone || '—'}</span>
          </div>
          <div className="fkv">
            <span className="k">Mode</span>
            <span className="v">{d.mode || '—'}</span>
          </div>
          {d.doorTime ? (
            <div className="fkv">
              <span className="k">Doors</span>
              <span className="v">{d.doorTime}</span>
            </div>
          ) : null}
          <div className="fkv">
            <span className="k">Free</span>
            <span className="v">{d.isFree ? 'Yes' : 'No'}</span>
          </div>
          {d.dateModified ? (
            <div className="fkv">
              <span className="k">Updated</span>
              <span className="v">{prettyTs(d.dateModified)}</span>
            </div>
          ) : null}
        </div>

        <div className="fsec">
          <div className="fsec-h">Where</div>
          <div className="fkv">
            <span className="k">Venue</span>
            <span className="v">{d.venueName || d.n || '—'}</span>
          </div>
          {d.address ? (
            <div className="fkv">
              <span className="k">Address</span>
              <span className="v">{d.address}</span>
            </div>
          ) : null}
          {d.postalCode ? (
            <div className="fkv">
              <span className="k">Postal code</span>
              <span className="v">{d.postalCode}</span>
            </div>
          ) : null}
          {d.coords ? (
            <div className="fkv">
              <span className="k">Coordinates</span>
              <span className="v">{d.coords}</span>
            </div>
          ) : null}
          {d.maxCapacity ? (
            <div className="fkv">
              <span className="k">Capacity</span>
              <span className="v">{d.maxCapacity}</span>
            </div>
          ) : null}
          {d.numUpcoming !== '' && d.numUpcoming != null ? (
            <div className="fkv">
              <span className="k">Upcoming</span>
              <span className="v">{d.numUpcoming}</span>
            </div>
          ) : null}
          {d.venueUrl ? (
            <div className="fkv">
              <span className="k">Venue link</span>
              <span className="v">
                <LinkBtn href={d.venueUrl}>Visit venue</LinkBtn>
              </span>
            </div>
          ) : null}
        </div>

        {offers.length ? (
          <div className="fsec">
            <div className="fsec-h">Tickets</div>
            <div className="ftickets">
              {offers.map((o, i) => (
                <div className="trow" key={i}>
                  <div className="k">
                    <b>{o.name || 'Tickets'}</b>
                    {o.price ? (
                      <span className="v" style={{ fontSize: 12 }}>
                        {o.price}
                      </span>
                    ) : null}
                    {o.availability ? (
                      <span style={{ fontSize: 11, color: 'var(--ea-fg-3)' }}>{o.availability}</span>
                    ) : null}
                  </div>
                  <LinkBtn href={o.url}>Get tickets</LinkBtn>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="fsec">
          <div className="fsec-h">Performers ({performers.length})</div>
          <div className="perf-list">
            {performers.length === 0 ? <span className="faint">No line-up published.</span> : performers.map(perfPill)}
          </div>
        </div>

        {organizers.length || sponsors.length ? (
          <div className="fsec">
            <div className="fsec-h">Organizer & sponsors</div>
            <div className="ftickets">
              {organizers.map((o, i) => (
                <div className="trow" key={'o' + i}>
                  <div className="k">
                    <b>{o.name || 'Organizer'}</b>
                    <span className="v" style={{ fontSize: 12 }}>
                      Organizer
                    </span>
                  </div>
                  {o.url ? <LinkBtn href={o.url}>Site</LinkBtn> : null}
                </div>
              ))}
              {sponsors.map((s, i) => (
                <div className="trow" key={'s' + i}>
                  <div className="k">
                    <b>{s.name || 'Sponsor'}</b>
                    <span className="v" style={{ fontSize: 12 }}>
                      Sponsor
                    </span>
                  </div>
                  {s.url ? <LinkBtn href={s.url}>Site</LinkBtn> : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {links.length ? (
          <div className="fsec">
            <div className="fsec-h">Links</div>
            <div className="links">
              {links.map((l, i) => (
                <LinkBtn key={i} href={l.href} muted>
                  {l.label}
                </LinkBtn>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
